/**
 * SeKa Baumaschinen - Scroll Animations
 * Uses IntersectionObserver for performant scroll-triggered animations
 */

(function () {
  'use strict';

  const SELECTOR_ANIMATE = '[data-animate], [data-animate-child]';
  const SELECTOR_STAGGER = '[data-animate-stagger]';
  const SELECTOR_COUNTER = '[data-count]';

  const config = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  let observer;
  let staggerObserver;
  let counterObserver;

  function makeVisible(el) {
    const delay = el.dataset.animateDelay || 0;
    const delayMs = parseInt(delay, 10) || 0;

    window.setTimeout(() => {
      el.classList.add('is-visible');
    }, delayMs);
  }

  function observeIn(root) {
    if (!root || root.nodeType !== 1 && root !== document) return;

    const scope = root === document ? document : root;

    // Base animations
    scope.querySelectorAll(SELECTOR_ANIMATE).forEach((el) => {
      // Prevent double-observing in design mode reloads
      if (el.dataset.sekaObserved === '1') return;
      el.dataset.sekaObserved = '1';
      observer.observe(el);
    });

    // Stagger containers
    scope.querySelectorAll(SELECTOR_STAGGER).forEach((container) => {
      if (container.dataset.sekaStaggerObserved === '1') return;
      container.dataset.sekaStaggerObserved = '1';
      staggerObserver.observe(container);
    });

    // Counters
    scope.querySelectorAll(SELECTOR_COUNTER).forEach((el) => {
      if (el.dataset.sekaCounterObserved === '1') return;
      el.dataset.sekaCounterObserved = '1';
      counterObserver.observe(el);
    });
  }

  function initObservers() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: show everything immediately
      document.querySelectorAll(SELECTOR_ANIMATE).forEach((el) => {
        el.classList.add('is-visible');
      });
      return;
    }

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        makeVisible(entry.target);
        observer.unobserve(entry.target);
      });
    }, config);

    staggerObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const container = entry.target;
        const children = container.querySelectorAll('[data-animate-child]');
        const baseDelay = parseInt(container.dataset.staggerDelay, 10) || 100;

        children.forEach((child, idx) => {
          window.setTimeout(() => {
            child.classList.add('is-visible');
          }, idx * baseDelay);
        });

        staggerObserver.unobserve(container);
      });
    }, config);

    counterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animateCounter(entry.target);
        counterObserver.unobserve(entry.target);
      });
    }, { threshold: 0.5 });
  }

  function initShopifyDesignModeHooks() {
    if (!(window.Shopify && Shopify.designMode)) return;

    const onSectionEvent = (e) => {
      // Shopify passes the section container as e.target
      observeIn(e.target || document);

      // In theme editor, ensure selected section becomes visible immediately
      if (e.target) {
        e.target.querySelectorAll(SELECTOR_ANIMATE).forEach((el) => el.classList.add('is-visible'));
      }
    };

    document.addEventListener('shopify:section:load', onSectionEvent);
    document.addEventListener('shopify:section:select', onSectionEvent);
    document.addEventListener('shopify:block:select', onSectionEvent);
  }

  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    if (parallaxElements.length === 0) return;

    let ticking = false;

    function updateParallax() {
      const scrollY = window.pageYOffset;

      parallaxElements.forEach((el) => {
        const speed = parseFloat(el.dataset.parallax) || 0.5;
        const rect = el.getBoundingClientRect();
        const offsetTop = rect.top + scrollY;
        const windowHeight = window.innerHeight;

        if (scrollY + windowHeight > offsetTop && scrollY < offsetTop + rect.height + windowHeight) {
          const yPos = (scrollY - offsetTop) * speed;
          el.style.transform = `translateY(${yPos}px)`;
        }
      });

      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      },
      { passive: true }
    );
  }

  function animateCounter(el) {
    const target = parseInt(el.dataset.count, 10) || 0;
    const duration = parseInt(el.dataset.countDuration, 10) || 2000;
    const start = 0;
    const startTime = performance.now();

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + (target - start) * easeOut);

      el.textContent = current.toLocaleString('de-DE');

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        el.textContent = target.toLocaleString('de-DE');
      }
    }

    requestAnimationFrame(update);
  }

  function init() {
    initObservers();
    observeIn(document);
    initShopifyDesignModeHooks();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
